import { TextField, TextFieldProps } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment, { Moment } from 'moment-timezone';
import React, { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { getRequiredValidationRule, getValidationErrorProps } from 'util/Form';
import ComponentProps from 'store/types/ComponentProps';
import { validateQuestionAnswer } from 'util/Application';

import styles from './DateFormItem.module.scss';

interface DateFormItemProps extends Pick<TextFieldProps, 'size' | 'disabled'>, ComponentProps {
  fieldName: string;
  label?: string;
  required?: boolean;
  maxDateErrorMessage?: string;
  minDateErrorMessage?: string;
  minDate?: Moment;
  maxDate?: Moment;
  validationRule?: string;
  validationErrorMessage?: string;
  onChange?: (value: Moment | string | null) => void;
}

const DateFormItem: React.FunctionComponent<DateFormItemProps> = ({
  fieldName,
  label = '',
  minDate,
  maxDate,
  required = true,
  size,
  disabled,
  maxDateErrorMessage,
  minDateErrorMessage,
  className,
  validationRule,
  validationErrorMessage,
  onChange,
}) => {
  const { control } = useFormContext();

  const validateField = useCallback(
    (inputValue?: Date) => {
      if (inputValue) {
        if (!moment(inputValue).isValid()) {
          return 'Please enter a valid date with this format (mm/dd/yyyy)';
        } else if (minDate && moment(inputValue).isBefore(moment(minDate))) {
          return minDateErrorMessage || 'Date should not be before minimal date';
        } else if (maxDate && moment(inputValue).isAfter(moment(maxDate))) {
          return maxDateErrorMessage || 'Date should not be after maximal date';
        }
        if (validationRule) {
          return validateQuestionAnswer(inputValue, validationRule, validationErrorMessage);
        }
      }
    },
    [minDate, maxDate, validationRule, minDateErrorMessage, maxDateErrorMessage, validationErrorMessage]
  );

  const handleChange = useCallback(
    (onInputChange) => (value: Moment | null) => {
      onInputChange(value);

      if (onChange) {
        onChange(value);
      }
    },
    [onChange]
  );

  return (
    <Controller
      render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
        <DatePicker
          views={['month', 'year', 'day']}
          label={label}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          value={value || null}
          onChange={handleChange(onChange)}
          onClose={onBlur}
          OpenPickerButtonProps={{ className: styles.keyboardButton }}
          renderInput={(props) => (
            <TextField
              {...props}
              {...getValidationErrorProps(error)}
              onBlur={onBlur}
              size={size}
              required={required}
              fullWidth={true}
              className={className}
              data-testid={'date-text-field'}
              inputProps={{
                ...props.inputProps,
                ['data-testid']: 'date-input',
              }}
              FormHelperTextProps={
                {
                  ['data-testid']: 'date-helper-text',
                } as any
              }
            />
          )}
        />
      )}
      name={fieldName}
      control={control}
      rules={{ required: getRequiredValidationRule(label.toLowerCase(), false, required), validate: validateField }}
    />
  );
};
export default DateFormItem;
